import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  FeedbackEmpty,
  FeedbackError,
  formatHandoverOdometer,
  InputSearch,
  PackageVehicleReasonList,
  SideBarModal,
  Skeleton,
  Status,
  TextareaField,
} from '@/components';
import { PACKAGE_STATUS_LABEL, PackageStatus } from '@/types/package';
import { ReassignCandidates, RentalDetail } from '@/types/rental';
import { getReassignCandidates } from '@/services/api/rentals';
import { extractApiErrorMessage } from '@/utils';

interface ReassignVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fires the REASSIGN_VEHICLE action; the parent owns reload + toasts. */
  onSubmit: (vehicleID: string, overrideReason?: string) => void;
  isLoading?: boolean;
  detail: RentalDetail;
}

// Below this the whole list fits on screen and a search box is noise.
const SEARCH_THRESHOLD = 5;

/**
 * Pre-pickup unit swap (TRD B6 damage-before-handover). Options come from the
 * reassign-candidates endpoint, never the fleet vehicle list, so a pick cannot
 * bounce off the swap's 422; a unit missing the rule on odometer, age or
 * condition only goes through with a reason (fleet #553).
 */
const ReassignVehicleModal: React.FC<ReassignVehicleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  detail,
}) => {
  // null until a response lands; failures keep it null and set loadError.
  const [result, setResult] = useState<ReassignCandidates | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedID, setSelectedID] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [query, setQuery] = useState('');

  const loadCandidates = useCallback(async () => {
    if (!detail.id) return;
    setIsFetching(true);
    setLoadError(null);
    try {
      setResult(await getReassignCandidates(detail.id));
    } catch (error) {
      setLoadError(extractApiErrorMessage(error, 'Coba muat ulang daftarnya.'));
      setResult(null);
    } finally {
      setIsFetching(false);
    }
  }, [detail.id]);

  // Fresh on every open: another ops user booking a unit invalidates the list.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedID('');
    setOverrideReason('');
    setQuery('');
    loadCandidates();
  }, [isOpen, loadCandidates]);

  const candidates = useMemo(() => result?.items ?? [], [result]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      [c.platNomor, c.hubName ?? ''].some((v) => v.toLowerCase().includes(q))
    );
  }, [candidates, query]);

  const selected = candidates.find((c) => c.id === selectedID) ?? null;
  const needsOverride = !!selected?.requiresOverride;
  const canSubmit =
    !!selectedID && !isLoading && (!needsOverride || !!overrideReason.trim());
  // The swap no longer cares (terms were frozen at booking); ops still should
  // know the package they are matching against is off.
  const packageStatus = result?.packageStatus;
  const currentPlate = detail.vehicle?.platNomor ?? '-';

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="md"
      header={
        <div>
          <h2 className="text-[18px] font-semibold text-gray-900">
            Ganti Unit
          </h2>
          <p className="text-[13px] text-gray-500 mt-0.5">{detail.code}</p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="medium" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            size="medium"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit(
                selectedID,
                needsOverride ? overrideReason.trim() : undefined
              )
            }
          >
            {isLoading ? 'Memproses...' : 'Ganti Unit'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 px-4 py-4 md:px-6 md:py-5">
        <div className="flex gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-[13px] text-gray-600">
          <i className="ri-information-line text-[16px] text-gray-400 leading-5"></i>
          <span>
            Unit hanya bisa diganti sebelum serah terima. Daftar berisi unit
            tersedia dengan model sama. Unit yang tidak lolos aturan odometer,
            umur, atau kondisi bisa dipilih dengan alasan.
          </span>
        </div>

        {packageStatus && packageStatus !== PackageStatus.ACTIVE && (
          <p className="text-[13px] text-gray-500">
            Package rental ini sedang {PACKAGE_STATUS_LABEL[packageStatus]}.
            Unit tetap bisa diganti; harga dan aturan mengikuti saat booking.
          </p>
        )}

        <section>
          <p className="text-[13px] font-medium text-gray-500 mb-2">
            Unit saat ini
          </p>
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100">
              <i className="ri-motorbike-line text-[20px] text-gray-500"></i>
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-gray-900">
                {currentPlate}
              </p>
              <p className="text-[13px] text-gray-500 truncate">
                {[detail.vehicle?.oemName, detail.vehicle?.modelName]
                  .filter(Boolean)
                  .join(' ') || '-'}
              </p>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[13px] font-medium text-gray-500">
              Pilih unit pengganti
            </p>
            {!isFetching && candidates.length > 0 && (
              <span className="text-[12px] text-gray-400">
                {candidates.length} unit tersedia
              </span>
            )}
          </div>

          {candidates.length > SEARCH_THRESHOLD && (
            <InputSearch
              value={query}
              onChange={setQuery}
              placeholder="Cari plat nomor atau hub"
              debounceMs={0}
              className="mb-2"
            />
          )}

          {isFetching ? (
            <div className="space-y-2" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[68px]" rounded="md" />
              ))}
            </div>
          ) : loadError ? (
            <FeedbackError
              message="Gagal memuat unit pengganti"
              description={loadError}
              onRetry={loadCandidates}
            />
          ) : candidates.length === 0 ? (
            <FeedbackEmpty
              size="sm"
              title="Tidak ada unit pengganti"
              description="Belum ada unit lain dengan model sama yang tersedia dan lolos aturan kepemilikan package ini."
            />
          ) : visible.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-gray-500">
              Tidak ada unit yang cocok dengan &ldquo;{query}&rdquo;
            </p>
          ) : (
            <div
              role="radiogroup"
              aria-label="Unit pengganti"
              className="space-y-2"
            >
              {visible.map((c) => {
                const isSelected = c.id === selectedID;
                const reasonsID = `reassign-reasons-${c.id}`;
                const hasReasons =
                  !!c.requiresOverride && (c.reasons ?? []).length > 0;
                return (
                  <div
                    key={c.id}
                    className={`rounded-xl border transition-colors ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-describedby={hasReasons ? reasonsID : undefined}
                      onClick={() => {
                        if (c.id !== selectedID) setOverrideReason('');
                        setSelectedID(c.id);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left"
                    >
                      <span
                        className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${
                          isSelected ? 'border-primary-600' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <span className="h-2 w-2 rounded-full bg-primary-600" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-gray-900">
                          {c.platNomor}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-map-pin-line"></i>
                            {c.hubName ?? 'Hub tidak diketahui'}
                          </span>
                          {c.odometer !== null && (
                            <span className="inline-flex items-center gap-1">
                              <i className="ri-dashboard-3-line"></i>
                              {formatHandoverOdometer(c.odometer)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <Status
                          type="vehicleCondition"
                          value={c.condition}
                          size="small"
                        />
                        {c.requiresOverride && (
                          <Status
                            type="packageVehicle"
                            value="REQUIRES_OVERRIDE"
                            size="small"
                          />
                        )}
                      </div>
                    </button>
                    {hasReasons && (
                      <PackageVehicleReasonList
                        id={reasonsID}
                        reasons={c.reasons ?? []}
                        tone="warning"
                        className="px-4 pb-3 pl-[46px] -mt-1.5"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {selected && (
          <div className="rounded-xl bg-primary-50 px-4 py-3">
            <div className="flex items-center gap-2 text-[14px] font-semibold text-gray-900">
              <span className="text-gray-500 line-through decoration-gray-400">
                {currentPlate}
              </span>
              <i className="ri-arrow-right-line text-primary-600"></i>
              <span>{selected.platNomor}</span>
            </div>
            <p className="mt-1 text-[13px] text-gray-600">
              QR yang discan saat serah terima harus milik unit baru.
            </p>
          </div>
        )}

        {needsOverride && (
          <section>
            <label
              htmlFor="reassign-override-reason"
              className="mb-1.5 block text-[13px] font-medium text-gray-700"
            >
              Alasan override <span className="text-red-500">*</span>
            </label>
            <TextareaField
              id="reassign-override-reason"
              value={overrideReason}
              onChange={setOverrideReason}
              rows={3}
              placeholder="Contoh: unit asli rusak, tidak ada unit lain yang lolos aturan"
            />
            <p className="mt-1 text-[12px] text-gray-500">
              Tercatat di riwayat ganti unit rental ini.
            </p>
          </section>
        )}
      </div>
    </SideBarModal>
  );
};

export default ReassignVehicleModal;
